import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import GenerateUploadLocForManualListDoc from 'bundles/enterprise-admin-messaging-v2/mutations/GenerateUploadLocForManualList.graphql';
import type {
  GenerateUploadLocForManualListMutation,
  GenerateUploadLocForManualListMutationVariables,
} from 'bundles/enterprise-admin-messaging-v2/mutations/__generated__/GenerateUploadLocForManualList';
import PreviewManualCsvImportDoc from 'bundles/enterprise-admin-messaging-v2/queries/PreviewManualCsvImport.graphql';
import type {
  PreviewManualCsvImportQuery,
  PreviewManualCsvImportQueryVariables,
} from 'bundles/enterprise-admin-messaging-v2/queries/__generated__/PreviewManualCsvImport';

/** S3 location of the uploaded CSV, threaded into the send payload's cohort context. */
export type ManualCsvS3Object = { bucket: string; key: string };

/**
 * Outcome of uploading + validating a manual-cohort CSV.
 * - `validLearnerCount` is set for a synchronous preview; `undefined` when the
 *   import validates asynchronously (count not yet known) — the success screen
 *   then shows a generic confirmation.
 */
export type UploadManualCsvResult =
  | { status: 'success'; s3Object: ManualCsvS3Object; validLearnerCount?: number }
  | { status: 'error'; error: string };

const CSV_CONTENT_TYPE = 'text/csv';

/**
 * Orchestrates the manual-cohort CSV upload, mirroring the invite-learner flow
 * with the messaging-specific endpoints:
 *   1. `GenerateUploadLocForManualList` → presigned S3 URL + target s3Location.
 *   2. PUT the file bytes to S3 (no progress tracking needed).
 *   3. `previewManualCsvImport` → validate the upload.
 *
 * Normalizes the sync/async preview union into a single discriminated result.
 * An async preview with `csvValidated: false`, a sync preview with no valid
 * learners, or any network/GraphQL failure all surface as an error so the
 * manual screen can show a single generic message.
 */
export const useUploadManualCsv = () => {
  const client = useApolloClient();
  const [isUploading, setIsUploading] = React.useState(false);

  const upload = React.useCallback(
    async (file: File, organizationId: string): Promise<UploadManualCsvResult> => {
      setIsUploading(true);
      try {
        const { data: locData } = await client.mutate<
          GenerateUploadLocForManualListMutation,
          GenerateUploadLocForManualListMutationVariables
        >({
          mutation: GenerateUploadLocForManualListDoc,
          variables: { input: { organizationId } },
          // Routes to the federated gateway (`/graphql-gateway`), where the
          // EnterpriseMessages schema lives — matching useAudienceLearners /
          // useAudienceCount in this bundle.
          context: { clientName: 'gatewayGql' },
          fetchPolicy: 'no-cache',
        });

        const uploadLoc = locData?.EnterpriseMessages_GenerateUploadLocForManualList;
        const s3Object = uploadLoc?.s3Location;
        const signedUploadUrl = uploadLoc?.signedUploadUrl;
        if (!s3Object || !signedUploadUrl) {
          return { status: 'error', error: 'Could not prepare the CSV upload.' };
        }

        const putResponse = await fetch(signedUploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': CSV_CONTENT_TYPE },
        });
        if (!putResponse.ok) {
          return { status: 'error', error: 'Could not upload the CSV file.' };
        }

        const { data: previewData } = await client.query<
          PreviewManualCsvImportQuery,
          PreviewManualCsvImportQueryVariables
        >({
          query: PreviewManualCsvImportDoc,
          variables: { organizationId, s3Bucket: s3Object.bucket, s3Key: s3Object.key },
          context: { clientName: 'gatewayGql' },
          fetchPolicy: 'no-cache',
        });

        const preview = previewData?.enterpriseMessages?.previewManualCsvImport;
        const result: ManualCsvS3Object = { bucket: s3Object.bucket, key: s3Object.key };

        if (preview?.__typename === 'EnterpriseMessages_SyncManualCsvImportPreview') {
          if (preview.validLearnerCount <= 0) {
            return { status: 'error', error: 'The CSV has no valid learners to message.' };
          }
          return { status: 'success', s3Object: result, validLearnerCount: preview.validLearnerCount };
        }

        if (preview?.__typename === 'EnterpriseMessages_AsyncManualCsvImportPreview') {
          if (!preview.csvValidated) {
            return { status: 'error', error: 'The CSV could not be validated.' };
          }
          return { status: 'success', s3Object: result };
        }

        return { status: 'error', error: 'Unexpected response while validating the CSV.' };
      } catch (e) {
        return { status: 'error', error: e instanceof Error ? e.message : 'Failed to upload the CSV.' };
      } finally {
        setIsUploading(false);
      }
    },
    [client]
  );

  return { upload, isUploading };
};

export default useUploadManualCsv;
